<template>
  <div
    ref="root"
    class="note-tags-field label-dropdown label-dropdown--flex"
    :class="{ 'note-tags-field--open': isOpen, 'label-dropdown--open': isOpen }"
  >
    <span :id="labelId" class="label-dropdown__label">{{ label }}</span>

    <div v-if="loading" class="note-tags-field__loading">Loading {{ relatedToLabel }} tags…</div>
    <div v-else-if="!availableTags.length && !allowCreate" class="note-tags-field__empty">No {{ relatedToLabel }} tags available.</div>

    <div v-else class="note-tags-field__picker">
      <div class="label-dropdown__control">
        <button
          :id="triggerId"
          type="button"
          class="note-tags-field__trigger label-dropdown__trigger"
          :class="{ 'note-tags-field__trigger--open': isOpen, 'label-dropdown__trigger--open': isOpen }"
          role="combobox"
          aria-haspopup="listbox"
          :aria-expanded="isOpen ? 'true' : 'false'"
          :aria-controls="listboxId"
          :aria-labelledby="labelId"
          @click="toggleOpen"
          @keydown="onTriggerKeydown"
        >
          <span class="note-tags-field__trigger-label label-dropdown__value">{{ triggerLabel }}</span>
          <i class="bi bi-chevron-down note-tags-field__chevron label-dropdown__chevron" aria-hidden="true"></i>
        </button>
      </div>

      <div
        v-if="selectedTags.length"
        class="note-tags-field__selected"
        role="group"
        :aria-label="`Selected ${relatedToLabel} tags`"
      >
        <button
          v-for="tag in selectedTags"
          :key="`selected-tag-${tag.id}`"
          type="button"
          class="note-tags-field__chip"
          :class="[
            `note-tags-field__chip--${entityType}`,
            'note-tags-field__chip--selected',
          ]"
          :style="chipStyle(tag)"
          :title="`Remove ${tag.name}`"
          @click="toggleTag(tag.id)"
        >
          <span
            class="note-tags-field__chip-strip"
            :style="{ backgroundColor: tag.color || defaultTagColor }"
            aria-hidden="true"
          ></span>
          <span class="note-tags-field__chip-label">{{ tag.name }}</span>
          <i class="bi bi-x-lg note-tags-field__chip-remove" aria-hidden="true"></i>
        </button>
      </div>

      <Teleport to="body">
        <Transition name="note-tags-field-menu">
          <div
            v-if="isOpen"
            ref="menuPanel"
            :id="listboxId"
            class="note-tags-field__menu-panel"
            :class="menuClass"
            :style="menuStyle"
            role="presentation"
            @mousedown.stop
          >
            <div v-if="showSearch" class="note-tags-field__search-wrap">
              <i class="bi bi-search note-tags-field__search-icon" aria-hidden="true"></i>
              <input
                ref="searchInput"
                type="search"
                class="note-tags-field__search"
                placeholder="Search tags..."
                :aria-label="`Search ${relatedToLabel} tags`"
                autocomplete="off"
                :value="searchQuery"
                @input="onSearchInput"
              />
              <button
                v-if="searchQuery"
                type="button"
                class="note-tags-field__search-clear"
                aria-label="Clear search"
                tabindex="-1"
                @mousedown.prevent
                @click="clearSearch"
              >
                <i class="bi bi-x" aria-hidden="true"></i>
              </button>
            </div>

            <ul
              class="note-tags-field__menu"
              role="listbox"
              :aria-label="`${relatedToLabel} tags`"
              aria-multiselectable="true"
            >
              <li
                v-for="tag in filteredTags"
                :key="tag.id"
                role="option"
                class="note-tags-field__option"
                :class="{
                  'note-tags-field__option--selected': isSelected(tag.id),
                  'note-tags-field__option--active': activeTagId === tag.id,
                }"
                :aria-selected="isSelected(tag.id) ? 'true' : 'false'"
                @mousedown.prevent="toggleTag(tag.id)"
                @mouseenter="activeTagId = tag.id"
              >
                <span
                  class="note-tags-field__option-strip"
                  :style="{ backgroundColor: tag.color || defaultTagColor }"
                  aria-hidden="true"
                ></span>
                <span class="note-tags-field__option-label">{{ tag.name }}</span>
                <i
                  class="bi note-tags-field__option-check"
                  :class="isSelected(tag.id) ? 'bi-check-square-fill' : 'bi-square'"
                  aria-hidden="true"
                ></i>
              </li>
              <li
                v-if="showCreateFromSearch"
                role="option"
                class="note-tags-field__option note-tags-field__option--create"
                :class="{ 'note-tags-field__option--active': activeTagId === '__create__' }"
                @mousedown.prevent="createTag(searchQuery.trim())"
                @mouseenter="activeTagId = '__create__'"
              >
                <i class="bi bi-plus-lg" aria-hidden="true"></i>
                Add "{{ searchQuery.trim() }}"
              </li>
              <li
                v-else-if="showNoResults"
                class="note-tags-field__option note-tags-field__option--empty"
                role="presentation"
              >
                No tags match your search
              </li>
            </ul>

            <div
              v-if="allowCreate"
              class="label-dropdown__create note-tags-field__create"
              @mousedown.stop
            >
              <input
                ref="createInput"
                v-model="createName"
                type="text"
                class="label-dropdown__create-input"
                :placeholder="`New ${relatedToLabel.toLowerCase()} tag`"
                :disabled="creating"
                autocomplete="off"
                @keydown.enter.prevent="createTag(createName.trim())"
              />
              <button
                type="button"
                class="label-dropdown__create-btn"
                :disabled="creating || !createNameTrimmed"
                title="Add tag"
                @click="createTag(createNameTrimmed)"
              >
                <i class="bi bi-plus-lg" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </div>
</template>

<script>
import noteTagService from '@/services/noteTag.service.js';
import logger from '@/utils/logger';

const RELATED_TO_LABELS = {
  customer: 'Customer',
  vendor: 'Vendor',
  consultant: 'Consultant',
};

export default {
  name: 'NoteTagsField',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
    entityType: {
      type: String,
      default: 'customer',
      validator: (value) => ['customer', 'vendor', 'consultant'].includes(value),
    },
    label: {
      type: String,
      default: 'Tags',
    },
    menuClass: {
      type: [String, Array, Object],
      default: '',
    },
    selectIdPrefix: {
      type: String,
      default: 'note-tags',
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'created'],
  data() {
    return {
      availableTags: [],
      loading: false,
      isOpen: false,
      searchQuery: '',
      createName: '',
      creating: false,
      menuStyle: {},
      activeTagId: null,
    };
  },
  computed: {
    defaultTagColor() {
      const colors = {
        customer: '#6366f1',
        vendor: '#10b981',
        consultant: '#f59e0b',
      };
      return colors[this.entityType] || '#6366f1';
    },
    relatedToLabel() {
      return RELATED_TO_LABELS[this.entityType] || 'Customer';
    },
    selectedTagIds() {
      if (!Array.isArray(this.modelValue)) return [];
      return this.modelValue.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    },
    selectedTags() {
      return this.availableTags.filter((tag) => this.isSelected(tag.id));
    },
    labelId() {
      return `${this.selectIdPrefix}-tags-label`;
    },
    triggerId() {
      return `${this.selectIdPrefix}-tags-trigger`;
    },
    listboxId() {
      return `${this.selectIdPrefix}-tags-listbox`;
    },
    showSearch() {
      return this.availableTags.length >= 3;
    },
    filteredTags() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.availableTags;
      return this.availableTags.filter((tag) =>
        String(tag.name || '').toLowerCase().includes(query)
      );
    },
    createNameTrimmed() {
      return this.createName.trim();
    },
    showCreateFromSearch() {
      if (!this.allowCreate || this.creating) return false;
      const query = this.searchQuery.trim();
      if (!query) return false;
      const lower = query.toLowerCase();
      return !this.availableTags.some(
        (tag) => String(tag.name || '').trim().toLowerCase() === lower
      );
    },
    showNoResults() {
      return this.searchQuery.trim() !== '' && this.filteredTags.length === 0 && !this.showCreateFromSearch;
    },
    triggerLabel() {
      const count = this.selectedTags.length;
      if (!count) return 'Select tags';
      if (count === 1) return this.selectedTags[0].name;
      return `${count} tags selected`;
    },
  },
  watch: {
    companyId: {
      handler() {
        this.loadTags();
      },
      immediate: true,
    },
    entityType() {
      this.loadTags();
    },
    isOpen(open) {
      if (open) {
        this.$nextTick(() => {
          this.updateMenuPosition();
          this.$nextTick(() => this.updateMenuPosition());
          this.attachPositionListeners();
          if (this.showSearch) this.focusSearchInput();
        });
      } else {
        this.detachPositionListeners();
      }
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleDocumentKeydown);
  },
  beforeUnmount() {
    this.close();
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  },
  methods: {
    isSelected(tagId) {
      return this.selectedTagIds.includes(Number(tagId));
    },
    chipStyle() {
      return {
        borderColor: '#cbd5e1',
        background: '#ffffff',
      };
    },
    async loadTags() {
      if (!this.companyId || !this.entityType) {
        this.availableTags = [];
        return;
      }

      this.loading = true;
      try {
        const response = await noteTagService.getNoteTags(this.companyId, this.entityType);
        this.availableTags = (response?.data?.data || []).filter(
          (tag) => tag.relatedTo === this.entityType
        );
      } catch (error) {
        logger.error('Failed to load note tags:', error);
        this.availableTags = [];
      } finally {
        this.loading = false;
      }
    },
    toggleTag(tagId) {
      const id = Number(tagId);
      const next = this.isSelected(id)
        ? this.selectedTagIds.filter((value) => value !== id)
        : [...this.selectedTagIds, id];
      this.$emit('update:modelValue', next);
    },
    onSearchInput(event) {
      this.searchQuery = event.target.value;
      this.activeTagId = this.filteredTags[0]?.id ?? null;
    },
    clearSearch() {
      this.searchQuery = '';
      this.focusSearchInput();
    },
    focusSearchInput() {
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    getTriggerElement() {
      return this.$refs.root?.querySelector('.note-tags-field__trigger') || null;
    },
    updateMenuPosition() {
      const trigger = this.getTriggerElement();
      if (!trigger || !this.isOpen) return;

      const rect = trigger.getBoundingClientRect();
      const panel = this.$refs.menuPanel;
      const menuHeight = panel?.offsetHeight || 220;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;

      this.menuStyle = {
        position: 'fixed',
        top: openUpward
          ? `${Math.max(8, rect.top - menuHeight - gap)}px`
          : `${rect.bottom + gap}px`,
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 200)}px`,
        zIndex: 10150,
      };
    },
    attachPositionListeners() {
      window.addEventListener('scroll', this.updateMenuPosition, true);
      window.addEventListener('resize', this.updateMenuPosition);
    },
    detachPositionListeners() {
      window.removeEventListener('scroll', this.updateMenuPosition, true);
      window.removeEventListener('resize', this.updateMenuPosition);
    },
    toggleOpen() {
      if (this.isOpen) {
        this.close();
        return;
      }
      this.isOpen = true;
      this.searchQuery = '';
      this.activeTagId = this.filteredTags[0]?.id ?? null;
    },
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.searchQuery = '';
      this.createName = '';
      this.activeTagId = null;
      this.menuStyle = {};
      this.detachPositionListeners();
    },
    async createTag(name) {
      const trimmed = String(name || '').trim();
      if (!this.allowCreate || !this.companyId || !trimmed || this.creating) return;

      const existing = this.availableTags.find(
        (tag) => String(tag.name || '').trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (existing) {
        if (!this.isSelected(existing.id)) {
          this.toggleTag(existing.id);
        }
        this.createName = '';
        this.searchQuery = '';
        return;
      }

      this.creating = true;
      try {
        const response = await noteTagService.createNoteTag({
          name: trimmed,
          companyId: this.companyId,
          relatedTo: this.entityType,
          color: this.defaultTagColor,
        });
        const tag = response?.data?.data || response?.data;
        if (tag?.id) {
          this.availableTags = [...this.availableTags, tag].sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
          );
          if (!this.isSelected(tag.id)) {
            this.toggleTag(tag.id);
          }
          this.$emit('created', tag);
          this.createName = '';
          this.searchQuery = '';
        }
      } catch (error) {
        logger.error('Failed to create note tag:', error);
      } finally {
        this.creating = false;
      }
    },
    handleClickOutside(event) {
      if (!this.isOpen) return;
      const root = this.$refs.root;
      const panel = this.$refs.menuPanel;
      if (root?.contains(event.target) || panel?.contains(event.target)) return;
      this.close();
    },
    handleDocumentKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    },
    onTriggerKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!this.isOpen) this.toggleOpen();
      }
    },
  },
};
</script>

<style src="@/modules/consultant/styles/LabelDropdown.css"></style>
<style src="@/styles/NoteTagsField.css"></style>
